var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PointCloudType } from "flavr-common";
import { BufferAttribute, BufferGeometry, Color, Float32BufferAttribute, Points, PointsMaterial, ShaderMaterial, Vector2 } from "three";
import { FacetRenderer } from "../rendering/renderable/Renderable";
import { meshLoaderService } from "../rendering/mesh/MeshLoaderService";
import { SharedArray, sharedArrayService } from "./sharedArray";
import { imageLoaderService } from "../rendering/image/ImageLoaderService";
import { CUSTOM_POINTS_SHADER } from "../rendering/shader/CustomPointsShader/CustomPointsShader";
const DEFAULT_POINT_SIZE = 0.05;
export class PointCloudRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.geometry = new BufferGeometry();
        this.counter = 0;
        this.isRegistered = false;
        this.onUpdate = () => {
            if (!this.sharedArray)
                return;
            if (!sharedArrayService.isSharedArrayBufferSupported())
                return;
            const numDirty = this.sharedArray.numDirty;
            if (numDirty !== 0) {
                this.geometry.attributes.position.needsUpdate = true;
                this.geometry.setDrawRange(0, numDirty);
                this.sharedArray.consumed();
                // this.geometry.attributes.color.needsUpdate = true;
            }
        };
        this.material = new PointsMaterial({
            size: 0.01,
            vertexColors: false,
            color: new Color(1, 1, 1)
        });
        this.customParticlesMaterial = new ShaderMaterial(CUSTOM_POINTS_SHADER);
        this.customParticlesMaterial.uniforms = Object.assign({}, this.customParticlesMaterial.uniforms);
        this.customParticlesMaterial.uniforms.size = { value: 0.01 };
        // this.customParticlesMaterial.uniforms.origin = { value: new Vector3() };
        this.customParticlesMaterial.uniforms.color = { value: new Color() };
        this.customParticlesMaterial.depthWrite = true;
        this.customParticlesMaterial.transparent = false;
        this.points = new Points(this.geometry, this.material);
        this.points.frustumCulled = false;
        this.points.matrixAutoUpdate = false;
        owner.addObjectToScene(this.points);
    }
    destroy() {
        this.stopTimer();
        this.geometry.dispose();
        this.material.dispose();
        this.customParticlesMaterial.dispose();
        this.owner.removeObjectFromScene(this.points);
        this.isDestroyed = true;
    }
    setVisible(visible) {
        this.points.visible = visible;
    }
    updateFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            this.facet = facet;
            if (!this.facet.params)
                return;
            if (!this.facet.params.color)
                this.facet.params.color = [1, 1, 1];
            if (!this.facet.params.pointSize)
                this.facet.params.pointSize = 0.25;
            if (facet.params.type === PointCloudType.RosBagBased && facet.params.useCustomMaterial) {
                this.customParticlesMaterial.uniforms = Object.assign({}, this.customParticlesMaterial.uniforms);
                this.customParticlesMaterial.uniforms.pointSize = {
                    value: this.facet.params.pointSize
                };
                this.customParticlesMaterial.uniforms.pointCloudOrigin = {
                    value: this.owner.worldPosition
                };
                if (this.facet.params.useVertexColor !== true)
                    this.customParticlesMaterial.uniforms.pointColor = {
                        value: new Color(this.facet.params.color[0], this.facet.params.color[1], this.facet.params.color[2])
                    };
                this.customParticlesMaterial.needsUpdate = true;
                this.points.material = this.customParticlesMaterial;
                this.points.material.needsUpdate = true;
            }
            else {
                this.points.material = this.material;
                this.points.material.needsUpdate = true;
                if (this.facet.params.useVertexColor === false)
                    this.material.color = new Color(this.facet.params.color[0], this.facet.params.color[1], this.facet.params.color[2]);
                this.material.needsUpdate = true;
                this.points.matrixWorldNeedsUpdate = true;
                this.material.size = this.facet.params.pointSize;
                this.points.visible = true;
            }
            switch (facet.params.type) {
                case PointCloudType.MeshBased:
                    yield this.updateMeshPointCloudFacet(facet);
                    break;
                case PointCloudType.ImageBased:
                    yield this.updateImageBasedPointCloudFacet(facet);
                    break;
                case PointCloudType.RosBagBased:
                    if (this.isRegistered)
                        return;
                    this.sharedArray = new SharedArray("PointCloud/RosBag", "PointCloudSharedArrayWorker", this.owner.modelUID);
                    this.sharedArray.onBufferChangedDelegate.set(() => {
                        if (!this.sharedArray)
                            return;
                        const lengthData = this.sharedArray.createDataArray(Uint32Array, 1);
                        const pointCloudData = this.sharedArray.createDataArray(Float32Array, lengthData[0]);
                        this.geometry.dispose();
                        this.geometry.setAttribute("position", new BufferAttribute(pointCloudData, 3));
                    });
                    this.isRegistered = true;
                    this.points.matrixWorldNeedsUpdate = true;
                    break;
                default:
                    break;
            }
        });
    }
    updateMeshPointCloudFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            const group = yield meshLoaderService.load(facet.params.url);
            if (group === undefined)
                return;
            this.material.size = facet.params.pointSize === undefined ? DEFAULT_POINT_SIZE : facet.params.pointSize;
            const data = meshLoaderService.verticesFromMesh(group, true);
            const color = facet.params.color;
            const gatheredVerticesFromAllMeshes = [];
            for (const vertexI of data.vertices) {
                gatheredVerticesFromAllMeshes.push(...vertexI);
            }
            const gatheredColorsFromAllMeshes = [];
            if (facet.params.useVertexColor === true) {
                for (const colorI of data.colors) {
                    gatheredColorsFromAllMeshes.push(...colorI);
                }
            }
            if (gatheredVerticesFromAllMeshes.length === 0)
                return;
            this.geometry.setAttribute("position", new Float32BufferAttribute(gatheredVerticesFromAllMeshes, 3));
            if (facet.params.useVertexColor === true && facet.params.color !== undefined && gatheredVerticesFromAllMeshes.length === gatheredColorsFromAllMeshes.length) {
                // Case use vertex colors from mesh
                this.material.vertexColors = true;
                this.geometry.setAttribute("color", new Float32BufferAttribute(gatheredColorsFromAllMeshes, 3));
            }
            else if (color !== undefined) {
                if (color.length === 3.0) {
                    // Case use single color from constructor
                    this.material.vertexColors = false;
                    this.material.color = new Color(color[0], color[1], color[2]);
                }
                else if (color.length > 3.0 && color.length % 3 === 0) {
                    // Case use multiple colors from constructor
                    this.material.vertexColors = true;
                    const colors = [];
                    let colorCounter = 0;
                    for (let i = 0; i < gatheredVerticesFromAllMeshes.length / 3; i++) {
                        colors.push(color[colorCounter * 3], color[colorCounter * 3 + 1], color[colorCounter * 3 + 2]);
                        colorCounter += 1;
                        colorCounter = colorCounter % (color.length / 3);
                    }
                    this.geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));
                }
            }
            this.material.needsUpdate = true;
        });
    }
    updateImageBasedPointCloudFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.facet)
                return;
            if (!this.facet.params.url)
                return;
            const pointCloud = yield this.loadTexturesFromFiles(this.facet.params.url);
            if (!pointCloud)
                return;
            if (this.facet !== facet)
                return;
            if (this.isDestroyed)
                return;
            this.material.size = facet.params.pointSize === undefined ? DEFAULT_POINT_SIZE : facet.params.pointSize;
            this.material.vertexColors = true;
            this.material.needsUpdate = true;
            this.startTimerPointCloud();
        });
    }
    updateTransform() {
        this.points.matrix.copy(this.owner.matrixWorld);
        this.points.matrixWorldNeedsUpdate = true;
    }
    stopTimer() {
        if (this.timeOutHandle !== undefined) {
            clearTimeout(this.timeOutHandle);
            this.timeOutHandle = undefined;
        }
    }
    // ImageBased funtions
    verticesAndColorsFromImage(image) {
        const vertices = [];
        const colors = [];
        if (!this.canvas)
            this.canvas = document.createElement("canvas");
        this.canvas.width = image.width;
        this.canvas.height = image.height;
        if (this.canvas) {
            const renderingContext = this.canvas.getContext("2d", { willReadFrequently: true });
            if (renderingContext) {
                renderingContext.drawImage(image, 0, 0, image.width, image.height);
                const minDegree = -80;
                const maxDegree = 80;
                const minRad = (minDegree * 3.14) / 180;
                const maxRad = (maxDegree * 3.14) / 180;
                for (let x = 0; x < image.width; x += 1) {
                    for (let y = 0; y < image.height; y += 1) {
                        const pixel = renderingContext.getImageData(x, y, 1, 1).data;
                        if (pixel[0] + pixel[1] + pixel[2]) {
                            // Get normalized position
                            const tAngle = x / image.width;
                            const tDistance = y / image.height;
                            // Interploate between poles
                            const valRad = minRad * (1 - tAngle) + maxRad * tAngle;
                            // Get direction vector
                            const direction = new Vector2(Math.cos(valRad), Math.sin(valRad));
                            // Multiply by distance
                            const finalPosition = new Vector2(tDistance * direction.x, tDistance * direction.y);
                            // Generate vertex
                            vertices.push(finalPosition.x); // x
                            vertices.push(0); // y
                            vertices.push(finalPosition.y); // z
                            // Get normalized color
                            colors.push(pixel[0] / 256);
                            colors.push(pixel[1] / 256);
                            colors.push(pixel[2] / 256);
                        }
                    }
                }
            }
        }
        return { vertices, colors };
    }
    startTimerPointCloud() {
        this.stopTimer();
        this.timeOutHandle = self.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getNextImagePointCloud();
            if (!data)
                return;
            this.updateImageBasedPointCloud(data);
            this.timeOutHandle = undefined;
            this.startTimerPointCloud();
        }), this.facet && this.facet.params.timeOutInterval ? this.facet.params.timeOutInterval : 100);
    }
    getNextImagePointCloud() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.facet || !this.facet.params.url)
                return;
            const dataArray = yield this.loadTexturesFromFiles(this.facet.params.url);
            if (!dataArray)
                return;
            const pointCloudData = dataArray[this.counter % dataArray.length];
            this.counter += 1;
            return pointCloudData;
        });
    }
    updateImageBasedPointCloud(data) {
        this.geometry.setAttribute("position", new Float32BufferAttribute(data.vertices, 3));
        this.geometry.setAttribute("color", new Float32BufferAttribute(data.colors, 3));
        this.material.vertexColors = true;
        // this.geometry.computeBoundingSphere();
    }
    loadTexturesFromFiles(folder) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.loadedPointCloudVertexData)
                return yield this.loadedPointCloudVertexData;
            const promises = [];
            for (let i = 1; i < 16; i += 1) {
                const combined = `${folder}RangeAngleImage_trial29_image${i}.png`;
                promises.push(imageLoaderService.load(combined).then((image) => this.verticesAndColorsFromImage(image)));
            }
            this.loadedPointCloudVertexData = Promise.all(promises);
            return yield this.loadedPointCloudVertexData;
        });
    }
    onInit(_) {
        if (sharedArrayService.isSharedArrayBufferSupported()) {
            this.registerAnimationCallback(this.onUpdate);
        }
    }
}
